<template>
  <div class="iw">
    <div ref="el" class="iw-el" />
    <div v-if="error" class="iw-err">{{ error }}</div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { h, render } from 'vue';
import { demoSafeInputs } from './frameTokens';
import { loadMountable } from './mountables';

let tip = null;

function tooltip() {
  if (!tip) {
    tip = document.createElement('div');
    tip.className = 'iw-tip';
    document.body.appendChild(tip);
  }
  return tip;
}

function placeTip(e) {
  const t = tooltip();
  const pad = 10;
  let x = e.clientX + 14;
  let y = e.clientY + 14;
  if (x + t.offsetWidth + pad > window.innerWidth) x = Math.max(pad, e.clientX - t.offsetWidth - 14);
  if (y + t.offsetHeight + pad > window.innerHeight) y = Math.max(pad, e.clientY - t.offsetHeight - 14);
  t.style.left = `${x}px`;
  t.style.top = `${y}px`;
}

export default {
  name: 'InlineWidget',
  props: {
    code: { type: String, required: true },
    inputs: { type: Object, default: () => ({}) },
    scope: { type: Object, default: () => ({}) },
    config: { type: Object, default: () => ({}) },
    filters: { type: Array, default: () => [] },
    memory: { type: Object, default: () => ({}) },
  },
  emits: ['export', 'drill-entity', 'select-patient', 'set-filter', 'refresh', 'empty'],
  data() {
    return { error: '', drawnWidth: 0 };
  },
  computed: {
    drawn() {
      return [this.code, this.inputs, this.scope, this.config, this.filters];
    },
  },
  watch: {
    drawn: { deep: true, handler() { this.draw(); } },
  },
  created() {
    this.mounts = [];
    this.cleanups = [];
  },
  mounted() {
    this.draw();
    this.resizer = new ResizeObserver(() => {
      const width = this.$refs.el ? this.$refs.el.clientWidth : 0;
      if (Math.abs(width - this.drawnWidth) > 2) this.draw();
    });
    this.resizer.observe(this.$el);
    this.$refs.el.addEventListener('mouseover', this.onOver);
    this.$refs.el.addEventListener('mousemove', this.onMove);
    this.$refs.el.addEventListener('mouseleave', this.hideTip);
  },
  beforeUnmount() {
    this.resizer.disconnect();
    this.hideTip();
    this.teardown();
  },
  methods: {
    teardown() {
      (this.mounts || []).forEach((m) => m.unmount());
      (this.cleanups || []).forEach((fn) => { try { fn(); } catch { /* the widget's own cleanup */ } });
      this.mounts = [];
      this.cleanups = [];
    },
    mount(name, target, props = {}, on = {}) {
      const listeners = Object.fromEntries(Object.entries(on).map(([event, fn]) => [
        'on' + event.replace(/(^|-)(\w)/g, (_, __, c) => c.toUpperCase()), fn]));
      const handle = { props: { ...props }, alive: true, component: null };
      const paint = () => {
        const vnode = h(handle.component, { ...handle.props, ...listeners });
        vnode.appContext = this.$.appContext;
        render(vnode, target);
      };
      handle.update = (next) => { handle.props = { ...handle.props, ...next }; if (handle.component && handle.alive) paint(); };
      handle.unmount = () => { if (handle.alive && handle.component) render(null, target); handle.alive = false; };
      this.mounts.push(handle);
      loadMountable(name).then((component) => {
        if (!handle.alive) return;
        if (!component) { target.textContent = `No component ${name} is available to widgets.`; return; }
        handle.component = component;
        paint();
      });
      return handle;
    },
    draw() {
      const el = this.$refs.el;
      if (!el) return;
      this.teardown();
      el.replaceChildren();
      this.error = '';
      const inputs = demoSafeInputs(this.inputs || {});
      const first = Object.keys(inputs)[0];
      const width = el.clientWidth;
      this.drawnWidth = width;
      const exportData = (spec) => this.$emit('export', JSON.parse(JSON.stringify(spec || {})));
      const emit = (name, payload) => {
        if (name === 'drill-entity' || name === 'select-patient') this.$emit(name, payload);
      };
      const mount = (name, target, props, on) => this.mount(name, target, props, on);
      const setFilter = (name, value) => this.$emit('set-filter', name, value);
      const cleanup = (fn) => { if (typeof fn === 'function') this.cleanups.push(fn); };
      const refresh = () => this.$emit('refresh');
      const filters = this.filters.map((f) => ({ ...f }));
      try {
        new Function('d3', 'data', 'inputs', 'el', 'width', 'exportData', 'scope', 'config', 'emit', 'mount', 'filters', 'setFilter', 'cleanup', 'refresh', 'memory', this.code)(
          d3, first ? inputs[first] : [], inputs, el, width, exportData, { ...this.scope }, { ...this.config }, emit,
          mount, filters, setFilter, cleanup, refresh, this.memory);
      } catch (e) {
        this.error = `${e && e.name ? e.name : 'Error'}: ${e && e.message ? e.message : String(e)}`;
      }
      this.$emit('empty', !this.error && !el.hasChildNodes());
    },
    tipTarget(node) {
      let n = node;
      while (n && n !== this.$refs.el && n.nodeType === 1) {
        if (n.hasAttribute('title') && n.getAttribute('title')) {
          n.setAttribute('data-tip', n.getAttribute('title'));
          n.removeAttribute('title');
        }
        if (n.getAttribute('data-tip')) return n;
        n = n.parentNode;
      }
      return null;
    },
    onOver(e) {
      this.target = this.tipTarget(e.target);
      if (!this.target) { this.hideTip(); return; }
      const t = tooltip();
      t.textContent = this.target.getAttribute('data-tip');
      t.style.display = 'block';
      placeTip(e);
    },
    onMove(e) {
      if (this.target) placeTip(e);
    },
    hideTip() {
      this.target = null;
      if (tip) tip.style.display = 'none';
    },
  },
};
</script>

<style scoped>
.iw { width: 100%; position: relative; flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; }
.iw-el { width: 100%; flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; }
.iw-err { padding: 12px; color: #c23a28; font-family: 'IBM Plex Mono', monospace; font-size: 12px; white-space: pre-wrap; }
</style>

<style>
.iw-tip { position: fixed; z-index: 2147483647; pointer-events: none; display: none; max-width: 320px; padding: 7px 9px; border-radius: 6px; background: #1A1A2E; color: #fff; font: 12px/1.4 -apple-system, system-ui, sans-serif; white-space: pre-line; box-shadow: 0 4px 14px rgba(26, 25, 24, 0.18); }
</style>
